/**
 * Organizations section entry (§2.10): /dashboard/organizations[?tab=] (list, screenshots/56–59) and
 * /dashboard/organizations/:id[?tab=] (profile, 60–65), plus /:id/brief (advocacy "Brief me", 66).
 * bootOrganizations() runs before the first render (main.tsx) and hydrates orgStore from the snapshots the
 * Worker inlines (snapshot:organizations, snapshot:organization:<id>), so the first frame has real data.
 */
import { useState } from "react";
import { ArrowLeft } from "lucide-react";
import { loadAsPage, navigate } from "../app/router";
import { connectionStore } from "../stores/connectionStore";
import { can, type OrgRole, type UserGroup } from "../nav/navConfig";
import { ORG_LIST_TABS, OrganizationsList, type OrgListTab } from "./OrganizationsList";
import { ORG_TABS, OrganizationProfile, type OrgTab } from "./OrganizationProfile";
import { orgStore, useOrgs } from "./orgStore";
import { andList, usd } from "./logic";
import { pct } from "../forecast/ForecastViews";
import type { OrgListData, OrgProfileSnapshot } from "./types";
import { Crumbs, sectionCrumb } from "../app/crumbs";

let bootedId: string | null = null;

export const orgIdOf = (path: string) => {
  const m = path.match(/^\/dashboard\/organizations\/([^/]+)/);
  return m ? decodeURIComponent(m[1]) : null;
};

export function bootOrganizations(path: string, inlined: <T>(id: string) => T | null) {
  const id = orgIdOf(path);
  if (id) {
    const snap = inlined<OrgProfileSnapshot | { error: number }>(`snapshot:organization:${id}`);
    if (snap && "org" in snap) {
      orgStore.getState().hydrateProfile(snap);
      bootedId = id;
      connectionStore.setState({ status: "snapshot", snapshotAt: snap.at });
    }
    return;
  }
  const list = inlined<OrgListData>("snapshot:organizations");
  if (list) {
    orgStore.getState().hydrateList(list);
    connectionStore.setState({ status: "snapshot", snapshotAt: list.at });
  }
}

const tabFrom = <T extends string>(all: readonly T[], fallback: T): T => {
  const t = new URLSearchParams(location.search).get("tab");
  return t && (all as readonly string[]).includes(t) ? (t as T) : fallback;
};

export function OrganizationsPage({ group, role, path }: { group: UserGroup; role: OrgRole | null; path: string }) {
  const id = orgIdOf(path);
  const [listTab, setListTab] = useState<OrgListTab>(() => tabFrom(ORG_LIST_TABS, "clients"));
  const [profileTab, setProfileTab] = useState<OrgTab>(() => tabFrom(ORG_TABS, "overview"));
  if (id) {
    // Navigation loads an organization's snapshot before it renders (main.tsx setPreparer); otherwise load it as a page.
    if (id !== bootedId) return loadAsPage();
    if (/\/brief\/?$/.test(path)) return can(group, role, "briefMe") ? <OrgBrief /> : <p className="empty-panel">Not available for your account.</p>;
    return <OrganizationProfile group={group} role={role} tab={profileTab} onTab={(t) => {
      setProfileTab(t);
      navigate(`/dashboard/organizations/${encodeURIComponent(id)}${t === "overview" ? "" : `?tab=${t}`}`, { replace: true });
    }} />;
  }
  if (!orgStore.getState().list) return loadAsPage();
  return <OrganizationsList group={group} role={role} tab={listTab} onTab={(t) => {
    setListTab(t);
    navigate(`/dashboard/organizations${t === "clients" ? "" : `?tab=${t}`}`, { replace: true });
  }} />;
}

/**
 * Brief me on an organization (advocacy; screenshots/66: "One page: what they want, who they fund, how members
 * tend to vote"). Not designed beyond that card: built from the profile's own data, no new claims.
 */
function OrgBrief() {
  const snap = useOrgs((s) => s.profile)!;
  const org = snap.org;
  const rows = org.forecastItem ? snap.forecasts[org.forecastItem.recordId] ?? [] : [];
  const lob = org.lobbyists.filter((l) => l.toAlderpersons > 0);
  return (
    <div className="page og-page og-brief">
      <Crumbs trail={[sectionCrumb("organizations"), { label: org.name, href: `/dashboard/organizations/${encodeURIComponent(org.id)}` }, { label: "Brief me" }]} />
      <h1 className="page-title">{org.name}</h1>
      <section><span className="og-eyebrow">What they lobby for</span>
        {org.interests && <p className="og-lede-lg">{org.interests}</p>}
        <ul>{org.asks.map((a) => <li key={a.title}><b>{a.title}.</b> {a.detail}</li>)}</ul></section>
      <section><span className="og-eyebrow">Who they fund</span>
        <p>{usd(org.money.company + org.money.employees + org.money.lobbyists)} to {org.alderpersonsFunded} alderpersons since 2019: {usd(org.money.company) === "—" ? "$0" : usd(org.money.company)} from the company, {usd(org.money.employees) === "—" ? "$0" : usd(org.money.employees)} from employees, {usd(org.money.lobbyists) === "—" ? "$0" : usd(org.money.lobbyists)} from its lobbyists{lob.length ? ` (${andList(lob.map((l) => l.name))})` : ""}.</p>
        <ul>{org.funded.slice(0, 5).map((m) => <li key={m.personId || m.name}>{m.name} · Ward {m.ward} · {usd(m.amount)}</li>)}</ul>
        {org.money.lobbyists > 0 && <p className="og-caveat">Lobbyists' own contributions count under every client they represent.</p>}</section>
      {org.forecastItem && rows.length > 0 && (
        <section><span className="og-eyebrow">How members tend to vote · {org.forecastItem.recordId}</span>
          <ul>{rows.map((r) => <li key={r.personId}>{r.name} · {pct(r.forecast.p)}% their way{org.forecastItem!.basis === "inferred" ? " (inferred position)" : ""}</li>)}</ul>
          <p className="og-caveat">Money near a vote isn’t evidence of influence.</p></section>
      )}
    </div>
  );
}
